import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  ListGroup,
  Modal,
  Button,
  Alert,
  Spinner,
  Badge,
} from "react-bootstrap";
import useApi from "../../lib/apiService.js";

function Events() {
  const Api = useApi();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    loadEvents();

    // Set up auto-refresh every 5 seconds
    const interval = setInterval(() => {
      loadEvents();
    }, 5000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const eventsList = await Api.getEventsList();
      setEvents(eventsList);
    } catch (err) {
      setError("Failed to load events: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = async (filename) => {
    try {
      setLoadingDetails(true);
      setSelectedEvent(filename);
      setShowModal(true);
      const details = await Api.getEventDetails(filename);
      setEventDetails(details);
    } catch (err) {
      setError("Failed to load event details: " + err.message);
      setEventDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
    setEventDetails(null);
  };

  const parseFilename = (filename) => {
    // Parse filename format: YYYYMMDD_HHMMSS_microseconds_eventId.json
    const parts = filename.replace(".json", "").split("_");
    if (parts.length >= 4) {
      const date = parts[0];
      const time = parts[1];
      const eventId = parts.slice(3).join("_"); // In case eventId contains underscores

      const year = date.substring(0, 4);
      const month = date.substring(4, 6);
      const day = date.substring(6, 8);
      const hour = time.substring(0, 2);
      const minute = time.substring(2, 4);
      const second = time.substring(4, 6);

      const formattedDate = `${year}-${month}-${day} ${hour}:${minute}:${second}`;

      return {
        date: formattedDate,
        eventId: eventId,
        displayName: `${eventId} - ${formattedDate}`,
      };
    }

    return {
      date: "Unknown",
      eventId: "Unknown",
      displayName: filename,
    };
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading events...</span>
          </Spinner>
          <p className="mt-2">Loading events...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Events</h1>
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted">Auto-refreshes every 5 seconds</small>
          <Button variant="outline-primary" onClick={loadEvents}>
            Refresh Now
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {events.length === 0 ? (
        <Card>
          <Card.Body>
            <Card.Text className="text-muted text-center">
              No events found. Events will appear here when they are received
              via the event sink endpoint.
            </Card.Text>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Header>
            <Card.Title className="mb-0">
              Event Files <Badge bg="secondary">{events.length}</Badge>
            </Card.Title>
          </Card.Header>
          <ListGroup variant="flush">
            {events.map((filename, index) => {
              const parsed = parseFilename(filename);
              return (
                <ListGroup.Item
                  key={index}
                  action
                  onClick={() => handleEventClick(filename)}
                  className="d-flex justify-content-between align-items-start"
                >
                  <div>
                    <div className="fw-bold">{parsed.eventId}</div>
                    <div className="text-muted small">{parsed.date}</div>
                  </div>
                  <Badge bg="light" text="dark" className="ms-2">
                    {filename}
                  </Badge>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Card>
      )}

      {/* Event Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Event Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <div className="mb-3">
              <strong>File:</strong> <code>{selectedEvent}</code>
            </div>
          )}

          {loadingDetails ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading event details...
            </div>
          ) : eventDetails ? (
            <div>
              <pre
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "1rem",
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                  maxHeight: "60vh",
                  overflow: "auto",
                }}
              >
                {JSON.stringify(eventDetails, null, 2)}
              </pre>
            </div>
          ) : (
            <Alert variant="warning">Failed to load event details.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Events;
